import BootstrapTable from 'react-bootstrap-table-next';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import filterFactory, { textFilter } from 'react-bootstrap-table2-filter';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { sortCaret, sizePerPageRenderer, paginationTotalRenderer } from '../../../utils/tableHelper';
import { stringOrDashFormatter, dateFormatter } from '../../../utils/formatHelper';
import { OrderSummaryModal } from '../../modals/OrderSummaryModal';
import Spinner from '../../common/Spinner';
import StatusBadge from '../../common/StatusBadge';
import { missingDataPlaceholder } from '../../../constants/missingDataPlaceholder';
import { retailerGetOrder, retailerOrderCloseModal } from '../../../store/actions/retailerOrders';
import { dropshipProductsUtils } from '../../../utils/dropship-products';

import styles from './OrdersTable.module.scss';
import { trimWhiteSpace } from '../../../utils/string';

function OrdersTable(props) {
  const { perPage, showStoreName, showSupplierColumn, statusCount = 0, offset = 0 } = props;
  const { orderTypeCounts } = useSelector((state) => state.dropshipOrders);

  const dispatch = useDispatch();
  const retailerOrders = useSelector((state) => state.retailerOrders);

  const [additionalOrderStates, setAdditionalOrderStates] = useState({
    displayStatus: null,
    infoStatus: null,
    postShipmentStatus: null,
  });

  const selectRowOptions = {
    hideSelectColumn: true,
    mode: 'radio',
    clickToSelect: true,
    onSelect: (row) => dispatch(retailerGetOrder(row)),
  };

  const shouldShowOrderTypeOption = dropshipProductsUtils.shouldShowOrderTypeOption(orderTypeCounts);

  const columns = [
    {
      text: '',
      dataField: 'retailer_order_id',
      headerClasses: 'filtered',
      filter: textFilter({
        placeholder: 'Order Id/ Sales Order Number',
        delay: 300,
        defaultValue: props.search,
      }),
    },
    {
      text: 'Date',
      dataField: 'created_at',
      formatter: dateFormatter,
      sort: true,
    },
    {
      text: 'Shipment status',
      dataField: 'display_status',
      formatter: (cell, row) => (
        <StatusBadge
          status={row.display_status}
          id={`retailer_processing_state_${row.id}`}
          className={styles.status}
          simplify
        />
      ),
      headerStyle: { width: '15%' },
    },
    {
      text: 'Info',
      dataField: 'info',
      formatter: (_cell, row) =>
        row.additional_info_status?.length ? (
          <StatusBadge
            status={row.additional_info_status}
            id={`retailer_info_state_${row.id}`}
            className={styles.status}
            simplify
            additionalInfo
          />
        ) : (
          missingDataPlaceholder
        ),
      headerStyle: { width: '15%' },
    },
    {
      text: 'Post-Shipment Status',
      dataField: 'post_shipment_state',
      formatter: (cell, row) =>
        row.post_shipment_status ? (
          <StatusBadge
            status={row.post_shipment_status}
            id={`retailer_post_shipment_state_${row.id}`}
            className={styles.status}
            simplify
            postShipmentStatus
          />
        ) : (
          missingDataPlaceholder
        ),
      headerStyle: { width: '15%' },
    },
  ];

  if (showStoreName) {
    columns.unshift({
      text: 'Store Name',
      dataField: 'store_name',
      formatter: stringOrDashFormatter,
    });
  }

  if (shouldShowOrderTypeOption) {
    columns.splice(columns.length - 1, 0, {
      text: 'Type',
      dataField: 'type',
      formatter: (cell, row) => {
        const type = row.type;
        return type ? (
          <StatusBadge status={type} isOrderTypeStatus id={`retailer_type_${row.id}`} className={styles.status} />
        ) : (
          missingDataPlaceholder
        );
      },
      headerStyle: { width: '15%' },
    });
  }

  if (showSupplierColumn) {
    columns.unshift({
      text: 'Seller',
      dataField: 'supplier_name',
    });
  }

  const noDataIndication = () => (props.isLoading ? <Spinner size="small" /> : `There are no orders to display`);

  const onTableChange = (type, newState) => {
    switch (type) {
      case 'sort':
        props.setSort(newState.sortOrder);
        break;

      case 'filter':
        let searchValue = '';
        const orderIdFilter = newState.filters.retailer_order_id;
        if (orderIdFilter) {
          searchValue = trimWhiteSpace(orderIdFilter.filterVal);
        }

        if (props.search !== searchValue) {
          props.setOffset(0);
          props.filterById(searchValue);
        }
        break;

      default:
        break;
    }
  };
  useEffect(() => {
    if (retailerOrders.order.current) {
      const specificOrder = props.data.find((order) => order.id === retailerOrders.order.current.id);
      const {
        display_status: displayStatus,
        additional_info_status: infoStatus,
        post_shipment_status: postShipmentStatus,
      } = specificOrder;
      setAdditionalOrderStates({ displayStatus, infoStatus, postShipmentStatus });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retailerOrders.order.current, props.data]);

  const onPageChange = (page) => {
    props.setOffset((page - 1) * perPage);
  };

  const pagination = paginationFactory({
    page: offset / perPage + 1,
    sizePerPage: perPage,
    sizePerPageList: [10, 25, 50],
    onPageChange: onPageChange,
    onSizePerPageChange: props.setPerPage,
    totalSize: statusCount,
    showTotal: true,
    sizePerPageRenderer,
    paginationTotalRenderer,
  });

  return (
    <div className={styles.container}>
      <BootstrapTable
        data={props.isLoading ? [] : props.data}
        columns={columns}
        keyField="id"
        striped
        hover
        bordered={false}
        selectRow={selectRowOptions}
        noDataIndication={noDataIndication}
        filter={filterFactory()}
        pagination={pagination}
        rowClasses={styles.pointerCursor}
        onTableChange={onTableChange}
        remote={{
          filter: true,
          pagination: true,
          sort: true,
        }}
        sort={{
          sortCaret: sortCaret,
        }}
        defaultSorted={[
          {
            dataField: 'created_at',
            order: props.sort,
          },
        ]}
      />

      <OrderSummaryModal
        opened={retailerOrders.modal.open}
        onClose={() => dispatch(retailerOrderCloseModal())}
        additionalOrderStates={additionalOrderStates}
      />
    </div>
  );
}

export default OrdersTable;

OrdersTable.propTypes = {
  setOffset: PropTypes.func.isRequired,
  filterById: PropTypes.func,
  data: PropTypes.array.isRequired,
  offset: PropTypes.number.isRequired,
  perPage: PropTypes.number.isRequired,
  setSort: PropTypes.func.isRequired,
  sort: PropTypes.string.isRequired,
  statusCount: PropTypes.number,
  showStoreName: PropTypes.bool.isRequired,
  showSupplierColumn: PropTypes.bool,
};
